import { createSignal, Show } from 'solid-js';
import Button from '../st-button/st-button';
import styles from './st-signature-modal.module.css';

export class SignatureModalProps {
    show: boolean = false;
    title?: string = '';
    message?: string= '';
    onSubmit: (signatureBase64: string) => void = (s: string) => { };
    onCancel: () => void = () => { };
}

const SIGNATURE_FONT = "'Brush Script MT', 'Brush Script Std', cursive";

// Custom signature pad implementation
class CustomSignaturePad {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private isDrawing = false;
    private isEmpty = true;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('Could not get canvas context');
        }
        this.ctx = context;

        // Set up canvas styles
        this.ctx.strokeStyle = '#000000';
        this.ctx.lineWidth = 2;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        // Fill background with white
        this.ctx.fillStyle = '#FFFFFF';
        this.ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Bind event listeners
        this.setupEventListeners();
    }

    private setupEventListeners() {
        // Mouse events
        this.canvas.addEventListener('mousedown', (e) => this.handleMouseDown(e));
        this.canvas.addEventListener('mousemove', (e) => this.handleMouseMove(e));
        this.canvas.addEventListener('mouseup', () => this.handleMouseUp());
        this.canvas.addEventListener('mouseleave', () => this.handleMouseUp());

        // Touch events for mobile
        this.canvas.addEventListener('touchstart', (e) => this.handleTouchStart(e));
        this.canvas.addEventListener('touchmove', (e) => this.handleTouchMove(e));
        this.canvas.addEventListener('touchend', () => this.handleMouseUp());
    }

    private getMousePos(e: MouseEvent): { x: number; y: number } {
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    }

    private getTouchPos(e: TouchEvent): { x: number; y: number } {
        const rect = this.canvas.getBoundingClientRect();
        const touch = e.touches[0];
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        return {
            x: (touch.clientX - rect.left) * scaleX,
            y: (touch.clientY - rect.top) * scaleY
        };
    }

    private handleMouseDown(e: MouseEvent) {
        this.isDrawing = true;
        this.isEmpty = false;

        const pos = this.getMousePos(e);

        // Start a new path
        this.ctx.beginPath();
        this.ctx.moveTo(pos.x, pos.y);
    }

    private handleMouseMove(e: MouseEvent) {
        if (!this.isDrawing) return;

        const pos = this.getMousePos(e);

        // Draw line to current position
        this.ctx.lineTo(pos.x, pos.y);
        this.ctx.stroke();
    }

    private handleMouseUp() {
        if (this.isDrawing) {
            this.isDrawing = false;
            this.ctx.closePath();
        }
    }

    private handleTouchStart(e: TouchEvent) {
        e.preventDefault();
        this.isDrawing = true;
        this.isEmpty = false;

        const pos = this.getTouchPos(e);

        // Start a new path
        this.ctx.beginPath();
        this.ctx.moveTo(pos.x, pos.y);
    }

    private handleTouchMove(e: TouchEvent) {
        if (!this.isDrawing) return;
        e.preventDefault();

        const pos = this.getTouchPos(e);

        // Draw line to current position
        this.ctx.lineTo(pos.x, pos.y);
        this.ctx.stroke();
    }

    clear() {
        this.ctx.fillStyle = '#FFFFFF';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.isEmpty = true;
    }

    isCanvasEmpty(): boolean {
        return this.isEmpty;
    }

    toDataURL(type: string = 'image/png'): string {
        return this.canvas.toDataURL(type);
    }

    toTrimmedDataURL(type: string = 'image/png', padding: number = 10): string {
        const pixels = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
        const data = pixels.data;
        let top = this.canvas.height, bottom = 0, left = this.canvas.width, right = 0;

        for (let y = 0; y < this.canvas.height; y++) {
            for (let x = 0; x < this.canvas.width; x++) {
                const idx = (y * this.canvas.width + x) * 4;
                const r = data[idx], g = data[idx + 1], b = data[idx + 2];
                // Non-white pixel
                if (r < 250 || g < 250 || b < 250) {
                    if (y < top) top = y;
                    if (y > bottom) bottom = y;
                    if (x < left) left = x;
                    if (x > right) right = x;
                }
            }
        }

        // If nothing drawn, return full canvas
        if (top > bottom || left > right) return this.canvas.toDataURL(type);

        const trimLeft = Math.max(0, left - padding);
        const trimTop = Math.max(0, top - padding);
        const trimWidth = Math.min(this.canvas.width, right + padding) - trimLeft;
        const trimHeight = Math.min(this.canvas.height, bottom + padding) - trimTop;

        const trimmed = document.createElement('canvas');
        trimmed.width = trimWidth;
        trimmed.height = trimHeight;
        const trimCtx = trimmed.getContext('2d')!;
        trimCtx.drawImage(this.canvas, trimLeft, trimTop, trimWidth, trimHeight, 0, 0, trimWidth, trimHeight);
        return trimmed.toDataURL(type);
    }
}

/** Renders typed text onto a hidden canvas and returns its base64 data URL. */
function renderTypedSignature(text: string): string {
    const font = `48px ${SIGNATURE_FONT}`;
    const padding = 20;

    // Measure the text dimensions using an off-screen canvas
    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d')!;
    measureCtx.font = font;
    const metrics = measureCtx.measureText(text);
    const textWidth = metrics.width;
    const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

    // Create a canvas sized to fit the text with padding
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(textWidth + padding * 2);
    canvas.height = Math.ceil(textHeight + padding * 2);
    const ctx = canvas.getContext('2d')!;

    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw the text centered
    ctx.fillStyle = '#000000';
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL('image/png');
}

export default function SignatureModal(props: SignatureModalProps) {
    let signaturePad: CustomSignaturePad | undefined;
    const [mode, setMode] = createSignal<'draw' | 'type'>('draw');
    const [typedName, setTypedName] = createSignal('');

    const setCanvasRef = (canvas: HTMLCanvasElement) => {
        if (canvas) {
            signaturePad = new CustomSignaturePad(canvas);
        }
    };

    const handleSubmit = () => {
        if (mode() === 'draw') {
            if (signaturePad && !signaturePad.isCanvasEmpty()) {
                const signatureDataUrl = signaturePad.toTrimmedDataURL('image/png');
                const signatureBase64 = signatureDataUrl.split(',')[1];
                props.onSubmit(signatureBase64);
                signaturePad.clear();
                setTypedName('');
            } else {
                alert('Please draw a signature before submitting.');
            }
        } else {
            if (typedName().trim()) {
                const dataUrl = renderTypedSignature(typedName().trim());
                const signatureBase64 = dataUrl.split(',')[1];
                props.onSubmit(signatureBase64);
                signaturePad?.clear();
                setTypedName('');
            } else {
                alert('Please type your name before submitting.');
            }
        }
    };

    const handleClear = () => {
        if (mode() === 'draw') {
            signaturePad?.clear();
        } else {
            setTypedName('');
        }
    };

    const handleCancel = () => {
        signaturePad?.clear();
        setTypedName('');
        setMode('draw');
        props.onCancel();
    };

    const switchMode = (newMode: 'draw' | 'type') => {
        if (newMode !== mode()) {
            signaturePad?.clear();
            setTypedName('');
            setMode(newMode);
        }
    };

    return (
        <Show when={props.show}>
            <div class={styles.overlay}>
                <div class={styles.modal}>
                    <h3 class={styles.title}>
                        {props.title || 'Please Sign Below'}
                    </h3>

                    {props.message && (
                        <p class={styles.message}>{props.message}</p>
                    )}

                    <div class={styles['mode-tabs']}>
                        <button
                            type='button'
                            class={`${styles['mode-tab']} ${mode() === 'draw' ? styles['mode-tab-active'] : ''}`}
                            onClick={() => switchMode('draw')}
                        >
                            <i class="fa fa-pencil" /> Draw
                        </button>
                        <button
                            type='button'
                            class={`${styles['mode-tab']} ${mode() === 'type' ? styles['mode-tab-active'] : ''}`}
                            onClick={() => switchMode('type')}
                        >
                            <i class="fa fa-keyboard" /> Type
                        </button>
                    </div>

                    <Show when={mode() === 'draw'}>
                        <div class={styles['canvas-container']}>
                            <canvas
                                ref={setCanvasRef}
                                width="560"
                                height="200"
                                class={styles.canvas}
                            />
                        </div>
                    </Show>

                    <Show when={mode() === 'type'}>
                        <div class={styles['type-container']}>
                            <div class={styles['type-preview']}>
                                <span class={styles['type-preview-text']}>
                                    {typedName() || 'Your signature'}
                                </span>
                            </div>
                            <input
                                type="text"
                                class={styles['type-input']}
                                placeholder="Type your full name"
                                value={typedName()}
                                onInput={(e) => setTypedName(e.currentTarget.value)}
                            />
                        </div>
                    </Show>

                    <div class={styles.actions}>
                        <Button type="default" onClick={handleClear}>
                            Clear
                        </Button>
                        <Button type="danger" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button type="success" onClick={handleSubmit}>
                            Submit
                        </Button>
                    </div>
                </div>
            </div>
        </Show>
    );
}
SignatureModal.defaultProps = new SignatureModalProps();
